/**
 * Thumbnail Generator (Client-side)
 * Generates deterministic gradient thumbnails with project title initials
 */

interface GradientColors {
  from: string;
  to: string;
}

// 26 gradient color combinations mapped to A-Z
const GRADIENT_PALETTE: GradientColors[] = [
  { from: '#8B5CF6', to: '#EC4899' }, // A - Purple to Pink
  { from: '#3B82F6', to: '#06B6D4' }, // B - Blue to Cyan
  { from: '#10B981', to: '#34D399' }, // C - Green to Emerald
  { from: '#F59E0B', to: '#F97316' }, // D - Amber to Orange
  { from: '#EF4444', to: '#F87171' }, // E - Red to Red Light
  { from: '#6366F1', to: '#8B5CF6' }, // F - Indigo to Purple
  { from: '#14B8A6', to: '#2DD4BF' }, // G - Teal to Teal Light
  { from: '#8B5CF6', to: '#A78BFA' }, // H - Purple to Purple Light
  { from: '#06B6D4', to: '#22D3EE' }, // I - Cyan to Cyan Light
  { from: '#10B981', to: '#6EE7B7' }, // J - Green to Green Light
  { from: '#F59E0B', to: '#FCD34D' }, // K - Amber to Amber Light
  { from: '#EC4899', to: '#F472B6' }, // L - Pink to Pink Light
  { from: '#6366F1', to: '#818CF8' }, // M - Indigo to Indigo Light
  { from: '#14B8A6', to: '#5EEAD4' }, // N - Teal to Teal Light
  { from: '#F97316', to: '#FB923C' }, // O - Orange to Orange Light
  { from: '#8B5CF6', to: '#C084FC' }, // P - Purple to Purple Light
  { from: '#3B82F6', to: '#60A5FA' }, // Q - Blue to Blue Light
  { from: '#EF4444', to: '#FCA5A5' }, // R - Red to Red Light
  { from: '#10B981', to: '#A7F3D0' }, // S - Green to Green Light
  { from: '#06B6D4', to: '#67E8F9' }, // T - Cyan to Cyan Light
  { from: '#8B5CF6', to: '#D8B4FE' }, // U - Purple to Purple Light
  { from: '#EC4899', to: '#FBCFE8' }, // V - Pink to Pink Light
  { from: '#3B82F6', to: '#93C5FD' }, // W - Blue to Blue Light
  { from: '#F59E0B', to: '#FDE68A' }, // X - Amber to Amber Light
  { from: '#6366F1', to: '#A5B4FC' }, // Y - Indigo to Indigo Light
  { from: '#14B8A6', to: '#99F6E4' }, // Z - Teal to Teal Light
];

/**
 * Get gradient colors based on the first character of the title
 */
function getGradientColors(title: string): GradientColors {
  if (!title || title.length === 0) {
    // Default gradient for empty titles
    return { from: '#8B5CF6', to: '#EC4899' };
  }

  const firstChar = title.charAt(0).toUpperCase();
  const charCode = firstChar.charCodeAt(0);

  // Check if it's A-Z
  if (charCode >= 65 && charCode <= 90) {
    return GRADIENT_PALETTE[charCode - 65];
  }

  // For non-alphabetic characters, use a hash-based selection
  const hash = title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return GRADIENT_PALETTE[hash % GRADIENT_PALETTE.length];
}

/**
 * Get the initial letter(s) to display on the thumbnail
 */
function getInitial(title: string): string {
  if (!title || title.length === 0) {
    return '?';
  }

  // Get first letter of first word
  const firstChar = title.trim().charAt(0).toUpperCase();

  // Check if it's alphanumeric
  if (/[A-Z0-9]/.test(firstChar)) {
    return firstChar;
  }

  // For special characters, try to find first alphanumeric
  const match = title.match(/[A-Z0-9]/i);
  return match ? match[0].toUpperCase() : '?';
}

/**
 * Generate an SVG gradient thumbnail with the project title initial
 * @param title - Project title
 * @param width - Thumbnail width (default: 640)
 * @param height - Thumbnail height (default: 360)
 * @returns SVG string
 */
export function generateGradientThumbnail(
  title: string,
  width: number = 640,
  height: number = 360
): string {
  const colors = getGradientColors(title);
  const initial = getInitial(title);

  // Calculate font size (proportional to height)
  const fontSize = Math.floor(height * 0.5);

  const svg = `
<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="grad-${title.replace(/\W/g, '')}" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:${colors.from};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${colors.to};stop-opacity:1" />
    </linearGradient>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#grad-${title.replace(/\W/g, '')})" />
  <text
    x="50%"
    y="50%"
    dominant-baseline="middle"
    text-anchor="middle"
    font-family="Arial, sans-serif"
    font-size="${fontSize}"
    font-weight="700"
    fill="white"
    opacity="0.9"
  >${initial}</text>
</svg>`.trim();

  return svg;
}

/**
 * Generate a data URL for the gradient thumbnail
 * @param title - Project title
 * @param width - Thumbnail width (default: 640)
 * @param height - Thumbnail height (default: 360)
 * @returns Data URL string suitable for img src attribute
 */
export function generateGradientThumbnailDataUrl(
  title: string,
  width: number = 640,
  height: number = 360
): string {
  const svg = generateGradientThumbnail(title, width, height);

  // Encode SVG for data URL
  // Using encodeURIComponent for better compatibility
  const encoded = encodeURIComponent(svg);

  return `data:image/svg+xml,${encoded}`;
}
